import React, { useState, useEffect, useContext } from 'react';
import { X, Trophy, Sparkles, Check, ArrowRight, RotateCcw, Volume2, Award, Brain, Filter } from 'lucide-react';
import { SessionSavedItem } from '../../services/friendSessionService';
import { SavedWord, ActivityType, TrainingSessionItem } from '../../types/vocabulary';
import {
  ReviewCard,
  SentenceFormation,
  ListenToWrite,
  Translation,
  ImageLinking,
  SpeakWord,
  SpeakPhrase,
  Synonyms,
  ListenAndArrange,
  FillInTheBlanks,
  Matching
} from '../vocabulary/activities/Activities';
import { updateUserScores } from '../../services/leaderboardService';
import { logUserActivity } from '../../services/activityLogService';
import { updateWordStats } from '../../services/vocabularyService';
import { useAuth } from '../../contexts/AuthContext';
import confetti from 'canvas-confetti';
import { sanitizeFirebaseKey } from '../../utils/firebaseUtils';

interface SessionTrainingModalProps {
  isOpen: boolean;
  onClose: () => void;
  savedItems: SessionSavedItem[];
  hostId: string;
  friendId: string;
  hostName: string;
  friendName: string;
}

export const SessionTrainingModal: React.FC<SessionTrainingModalProps> = ({
  isOpen,
  onClose,
  savedItems,
  hostId,
  friendId,
  hostName,
  friendName,
}) => {
  const { currentUser } = useAuth();
  const [filter, setFilter] = useState<'all' | 'me' | 'friend' | 'both'>('all');
  const [sessionQueue, setSessionQueue] = useState<TrainingSessionItem[]>([]);
  const [currentWordIdx, setCurrentWordIdx] = useState(0);
  const [currentActivityIdx, setCurrentActivityIdx] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [sessionPoints, setSessionPoints] = useState(0);
  const [historyResults, setHistoryResults] = useState<Array<{ word: string; activity: ActivityType; success: boolean }>>([]);

  const isHost = currentUser?.uid === hostId;
  const myPartnerName = isHost ? friendName : hostName;

  // Filter items
  const eligibleItems = savedItems.filter((item) => {
    if (filter === 'all') return true;
    if (filter === 'both') return item.assignedTo === 'both';
    if (filter === 'me') {
      return item.assignedUserId === currentUser?.uid || item.assignedTo === 'both';
    }
    if (filter === 'friend') {
      const partnerId = isHost ? friendId : hostId;
      return item.assignedUserId === partnerId;
    }
    return true;
  });

  // Build training activities queue
  const startTraining = (itemsToTrain: SessionSavedItem[]) => {
    if (itemsToTrain.length === 0) return;

    const queue: TrainingSessionItem[] = itemsToTrain.map((item) => {
      const savedWord: SavedWord = {
        id: item.id || sanitizeFirebaseKey(item.word),
        userId: currentUser?.uid || '',
        word: item.word,
        translation: item.translation,
        exampleSentence: item.exampleSentence,
        exampleTranslation: item.exampleTranslation,
        literalMeaning: item.literalMeaning,
        progress: 0,
        createdAt: item.createdAt,
        lastPractice: Date.now(),
        synonyms: [],
        successCount: 0,
        failCount: 0,
        type: item.type === 'expression' ? 'expression' : 'single_word',
      };

      // Select diverse activities
      const activities: ActivityType[] = [ActivityType.Review];

      if (item.type === 'expression' || item.word.split(/\s+/).length > 1) {
        activities.push(ActivityType.SpeakPhrase);
        if (item.translation) activities.push(ActivityType.Translation);
        activities.push(ActivityType.ListenToWrite);
      } else {
        activities.push(ActivityType.SpeakWord);
        if (item.translation) activities.push(ActivityType.Translation);
        if (item.exampleSentence && item.exampleSentence.trim().length > 5) {
          activities.push(ActivityType.ListenAndArrange);
          activities.push(ActivityType.FillInTheBlanks);
        } else {
          activities.push(ActivityType.ListenToWrite);
          activities.push(ActivityType.SentenceFormation);
        }
      }

      return {
        word: savedWord,
        activities,
      };
    });

    setSessionQueue(queue);
    setCurrentWordIdx(0);
    setCurrentActivityIdx(0);
    setIsCompleted(false);
    setSessionPoints(0);
    setHistoryResults([]);
  };

  useEffect(() => {
    if (isOpen && savedItems.length > 0 && sessionQueue.length === 0) {
      startTraining(eligibleItems);
    }
  }, [isOpen, filter, savedItems]);

  const handleActivityComplete = async (success: boolean) => {
    const currentItem = sessionQueue[currentWordIdx];
    if (!currentItem) return;

    const currentActivity = currentItem.activities[currentActivityIdx];
    const earned = success ? 10 : 2;
    setSessionPoints((prev) => prev + earned);

    setHistoryResults((prev) => [
      ...prev,
      {
        word: currentItem.word.word,
        activity: currentActivity,
        success,
      },
    ]);

    // Update word stats defensively in background
    if (currentUser?.uid) {
      updateWordStats(currentUser.uid, currentItem.word.word, { success }).catch(console.error);
    }

    // Advance to next activity or next word
    if (currentActivityIdx < currentItem.activities.length - 1) {
      setCurrentActivityIdx((prev) => prev + 1);
    } else if (currentWordIdx < sessionQueue.length - 1) {
      setCurrentWordIdx((prev) => prev + 1);
      setCurrentActivityIdx(0);
    } else {
      // Finished all activities!
      setIsCompleted(true);

      // Trigger celebratory confetti
      confetti({
        particleCount: 80,
        spread: 60,
        origin: { y: 0.6 },
      });

      // Award grouped points and log user activity at the end of session
      if (currentUser?.uid) {
        const totalAward = Math.max(50, (sessionPoints + earned) * 2);
        try {
          await updateUserScores(currentUser.uid, totalAward);
          await logUserActivity({
            userId: currentUser.uid,
            type: 'training_completed',
            points: totalAward,
            details: `Completed Friend Session training for ${sessionQueue.length} words`,
          });
        } catch (err) {
          console.error('Error recording session completion scores:', err);
        }
      }
    }
  };

  if (!isOpen) return null;

  const currentItem = sessionQueue[currentWordIdx];
  const currentActivity = currentItem?.activities[currentActivityIdx];

  const totalActivitiesInSession = sessionQueue.reduce((acc, curr) => acc + curr.activities.length, 0);
  const completedActivitiesCount = sessionQueue
    .slice(0, currentWordIdx)
    .reduce((acc, curr) => acc + curr.activities.length, 0) + currentActivityIdx;

  const progressPercent = totalActivitiesInSession > 0
    ? Math.round((completedActivitiesCount / totalActivitiesInSession) * 100)
    : 0;

  const renderCurrentActivity = () => {
    if (!currentItem || !currentActivity) return null;

    const props = {
      word: currentItem.word,
      onComplete: handleActivityComplete,
    };

    switch (currentActivity) {
      case ActivityType.Review:
        return <ReviewCard {...props} />;
      case ActivityType.SentenceFormation:
        return <SentenceFormation {...props} />;
      case ActivityType.ListenToWrite:
        return <ListenToWrite {...props} />;
      case ActivityType.Translation:
        return <Translation {...props} />;
      case ActivityType.ImageLinking:
        return <ImageLinking {...props} />;
      case ActivityType.SpeakWord:
        return <SpeakWord {...props} />;
      case ActivityType.SpeakPhrase:
        return <SpeakPhrase {...props} />;
      case ActivityType.Synonyms:
        return <Synonyms {...props} />;
      case ActivityType.ListenAndArrange:
        return <ListenAndArrange {...props} />;
      case ActivityType.FillInTheBlanks:
        return <FillInTheBlanks {...props} />;
      case ActivityType.Matching:
        return <Matching {...props} />;
      default:
        return <ReviewCard {...props} />;
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 md:p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-purple-50 via-indigo-50 to-pink-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center shadow-sm">
              <Brain className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
                Session Training
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold">
                  {savedItems.length} words in session
                </span>
              </h3>
              <p className="text-xs text-gray-500">
                Play training activities for the words and expressions saved with your friend
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-white/80 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filter bar when not completed */}
        {!isCompleted && (
          <div className="p-3 border-b border-gray-100 bg-gray-50/50 flex flex-wrap items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-1">
              <span className="font-medium text-gray-500 mr-1 flex items-center gap-1">
                <Filter className="w-3.5 h-3.5" /> Train:
              </span>
              <button
                onClick={() => {
                  setFilter('all');
                  startTraining(savedItems);
                }}
                className={`px-2.5 py-1 rounded-lg font-medium transition-colors ${
                  filter === 'all'
                    ? 'bg-purple-600 text-white shadow-xs'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                All ({savedItems.length})
              </button>
              <button
                onClick={() => {
                  setFilter('me');
                  startTraining(
                    savedItems.filter(
                      (i) => i.assignedUserId === currentUser?.uid || i.assignedTo === 'both'
                    )
                  );
                }}
                className={`px-2.5 py-1 rounded-lg font-medium transition-colors ${
                  filter === 'me'
                    ? 'bg-purple-600 text-white shadow-xs'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                For Me
              </button>
              <button
                onClick={() => {
                  setFilter('both');
                  startTraining(savedItems.filter((i) => i.assignedTo === 'both'));
                }}
                className={`px-2.5 py-1 rounded-lg font-medium transition-colors ${
                  filter === 'both'
                    ? 'bg-purple-600 text-white shadow-xs'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                For Both
              </button>
            </div>

            {/* Progress indicator */}
            <div className="flex items-center gap-2">
              <div className="w-24 bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-purple-600 h-2 transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-gray-500 font-medium">
                {completedActivitiesCount}/{totalActivitiesInSession} ({progressPercent}%)
              </span>
            </div>
          </div>
        )}

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 flex flex-col justify-center">
          {savedItems.length === 0 ? (
            <div className="text-center py-12">
              <Brain className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h4 className="text-lg font-bold text-gray-700">No words saved yet</h4>
              <p className="text-sm text-gray-500 max-w-sm mx-auto mt-1">
                Click on any word or select an expression in the study article to save and assign it, then return here to train!
              </p>
            </div>
          ) : isCompleted ? (
            /* Training Complete Summary */
            <div className="text-center py-8 space-y-5 animate-in zoom-in-95 duration-200">
              <div className="w-16 h-16 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mx-auto shadow-md">
                <Trophy className="w-9 h-9" />
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900">Training Session Completed! 🎉</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Great job practicing with {myPartnerName}! Your scores and word progress have been saved.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto">
                <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                  <p className="text-xs uppercase tracking-wider text-purple-600 font-bold">Items Practiced</p>
                  <p className="text-2xl font-extrabold text-purple-900 mt-1">{sessionQueue.length}</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-xl border border-amber-100">
                  <p className="text-xs uppercase tracking-wider text-amber-600 font-bold">Points Earned</p>
                  <p className="text-2xl font-extrabold text-amber-900 mt-1">+{Math.max(50, sessionPoints * 2)}</p>
                </div>
              </div>

              <div className="flex justify-center gap-3 pt-3">
                <button
                  onClick={() => startTraining(eligibleItems)}
                  className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 font-semibold text-sm flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" /> Train Again
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 rounded-xl bg-purple-600 text-white hover:bg-purple-700 font-semibold text-sm shadow-md flex items-center gap-2"
                >
                  <span>Back to Session</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            /* Active Activity Container */
            <div className="w-full max-w-2xl mx-auto">
              <div className="mb-4 flex items-center justify-between text-xs text-gray-400">
                <span className="font-semibold uppercase text-purple-600">
                  Word {currentWordIdx + 1} of {sessionQueue.length}: "{currentItem?.word.word}"
                </span>
                <span>
                  Activity {currentActivityIdx + 1} of {currentItem?.activities.length}
                </span>
              </div>

              <div className="bg-gray-50/70 p-4 md:p-6 rounded-2xl border border-gray-100 shadow-inner min-h-[320px] flex flex-col justify-center">
                {renderCurrentActivity()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
